import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	Cashdesk,
	createWithdrawCashdeskMutation,
	UuidResponse,
	WithdrawCashdeskMutationParams,
} from 'shared/api';
import { createModalFormModel, ModalFormModel, notEmptyRule, notify } from 'shared/lib';

export type WithdrawCashdeskFormValue = {
	amount: string;
};

export type WithdrawCashdeskModel = ModalFormModel<Cashdesk, WithdrawCashdeskFormValue> & {
	withdrawCashdeskMutation: Mutation<
		WithdrawCashdeskMutationParams,
		UuidResponse,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createWithdrawCashdeskModel = (): WithdrawCashdeskModel => {
	const withdrawCashdeskMutation = createWithdrawCashdeskMutation();

	const model = createModalFormModel<Cashdesk, WithdrawCashdeskFormValue>({
		fields: {
			amount: {
				init: '',
				rules: [notEmptyRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const { $pending } = withdrawCashdeskMutation;

	sample({
		clock: model.form.submitted,
		source: {
			amount: model.form.fields.amount.$value,
			cashdesk: model.$modalData,
		},
		filter: ({ cashdesk }) => cashdesk !== null,
		fn: ({ amount, cashdesk }) => ({
			amount: Number(amount),
			cashdeskId: cashdesk!.id,
		}),
		target: withdrawCashdeskMutation.start,
	});

	chain(withdrawCashdeskMutation.finished.success, model.switcher.disable);

	notify(withdrawCashdeskMutation.finished.success, 'withdraw-cashdesk.success');

	chain(model.modalClosed, withdrawCashdeskMutation.reset);

	return {
		withdrawCashdeskMutation,
		$pending,
		...model,
	};
};
